import { z } from 'zod';
import { ALL_ALLERGENS, ALL_PRODUCT_CATEGORIES, ALL_UNITS_OF_MEASURE, QUANTITY_MAX_DECIMALS } from './enums';
import { QUANTITY_MAX } from './labelQuantity';
import { ApiScope } from './publicApi';
import { MAX_VALIDITY_DURATION_HOURS } from './schemas';
import { WEBHOOK_API_VERSION } from './webhooks';

/**
 * Webhooks de ENTRADA — o sistema do parceiro manda eventos para nós, no mesmo
 * formato do webhook de SAÍDA (#792), e nós gravamos.
 *
 * Origem: #803 — 2026-10-04 14:48 +0200 (owner: "o parceiro deve criar webhook
 * no sistema dele no mesmo padrão do nosso webhook, e poder ser assinado em
 * developers").
 *
 * Por quê: até aqui a integração era só de leitura (API v1 `GET` e webhooks de
 * saída). Um parceiro que já consome o nosso webhook reaproveita o MESMO código
 * para emitir: envelope `{ id, type, apiVersion, occurredAtMillis, tenantId,
 * data: { object } }`, headers Standard Webhooks (`webhook-id`,
 * `webhook-timestamp`, `webhook-signature`), segredo `whsec_`. É a primeira
 * porta de ESCRITA externa do sistema — por isso a entrada é estrita por
 * construção: o owner exigiu (2026-10-04) que TODA possibilidade de dado ruim
 * esteja mapeada antes de aceitar uma mensagem.
 *
 * Contrato (este módulo é a fonte única de tudo que o parceiro vê):
 *  - `InboundWebhookEventType`: os 7 eventos aceitos na v1 (etiquetas e produtos,
 *    decisão do owner 2026-10-04) — mesmos nomes do catálogo de saída.
 *  - `INBOUND_CHECKS`: o MAPA DE CHECAGEM, em ordem de execução. Cada linha tem
 *    id (`A1`…`I3`), fase, desfecho (erro/aviso/sucesso), HTTP e "como tratar".
 *    É consumido pelo pipeline (trace), pela bancada de testes do portal, pela
 *    doc do portal e pelo gate de cobertura (todo check tem cenário).
 *  - `InboundErrorCode` / `InboundWarningCode` / `InboundPayloadIssue`: unions
 *    fechadas com HTTP e mensagem — o `code` é contrato, a `message` é ajuda.
 *  - Schemas ESTRITOS do envelope e de cada `data.object`: campo desconhecido
 *    reprova, não há coerção (`"3"` não vira `3`), `quantity` e `unit` da baixa
 *    são obrigatórias (nunca "saldo inteiro por omissão").
 *  - Documentos `inbound_webhook_endpoints` e `inbound_webhook_events`, e o
 *    contrato da parte "inbound" da callable `devPortalWebhooks`.
 *
 * Usado por: Functions — `webhooks/inbound/*` (pipeline, HTTP, fixtures da
 * bancada) e `devPortal/inboundWebhooks.ts` (ações do portal); portal
 * `apps/developers` (telas `InboundWebhook*Page`, `InboundTestBench`,
 * `InboundChecksTable`); `scripts/gen-portal-content.ts` (doc e OpenAPI).
 *
 * Arquitetura: a validação de FORMATO mora aqui (pura, sem Firestore); a de
 * ESTADO (etiqueta aberta, saldo, teto de produtos) mora no pipeline das
 * Functions, dentro da transação. Os limites de produto vêm das mesmas
 * constantes de `CreateProductInput` — um limite apertado lá aperta aqui.
 *
 * Dados — exemplo de mensagem do parceiro (anonimizado, formato da v1):
 *   POST /v1/inbound/<endpointId>
 *   webhook-id: evt_partner_000001
 *   webhook-timestamp: 1791100000            // SEGUNDOS
 *   webhook-signature: v1,<base64 HMAC-SHA256 de "id.timestamp.corpo">
 *   {
 *     "id": "evt_partner_000001",            // = webhook-id
 *     "type": "label.consumed",
 *     "apiVersion": "v1",
 *     "occurredAtMillis": 1791099990000,     // MILISSEGUNDOS — quando aconteceu no parceiro
 *     "tenantId": "tenant-1",
 *     "data": { "object": { "labelId": "lbl_1", "quantity": 1.5, "unit": "KILOGRAM" } }
 *   }
 *
 * Falhas conhecidas: nenhuma ainda (módulo novo). Armadilhas herdadas que o
 * desenho evita: timestamp em ms no header (o Standard Webhooks usa segundos) e
 * `JSON.parse` aceitando chave duplicada em silêncio.
 *
 * Dicas: check novo = linha em `INBOUND_CHECKS` + cenário em
 * `INBOUND_TEST_SCENARIOS` (`inboundWebhookScenarios.ts`) + builder em
 * `apps/functions/src/webhooks/inbound/scenarios.ts`; o teste de cobertura
 * reprova se faltar qualquer um dos três.
 *
 * Issues: #803 (origem), #792 (saída, mesmo envelope e assinatura), #323/#327
 * (baixa parcial por quantidade).
 */

// ---------------------------------------------------------------------------
// Eventos
// ---------------------------------------------------------------------------

export const InboundWebhookEventType = {
  LABEL_CONSUMED: 'label.consumed',
  LABEL_DISCARDED: 'label.discarded',
  LABEL_UNDONE: 'label.undone',
  PRODUCT_CREATED: 'product.created',
  PRODUCT_UPDATED: 'product.updated',
  PRODUCT_ACTIVATED: 'product.activated',
  PRODUCT_DEACTIVATED: 'product.deactivated',
} as const;
export type InboundWebhookEventType =
  (typeof InboundWebhookEventType)[keyof typeof InboundWebhookEventType];
export const ALL_INBOUND_WEBHOOK_EVENT_TYPES = Object.values(InboundWebhookEventType) as [
  InboundWebhookEventType,
  ...InboundWebhookEventType[],
];
export const InboundWebhookEventTypeSchema = z.enum(ALL_INBOUND_WEBHOOK_EVENT_TYPES);

/** Guarda de tipo do evento de entrada. Usado por: `webhooks/inbound/payload.ts`. */
export function isInboundWebhookEventType(value: unknown): value is InboundWebhookEventType {
  return typeof value === 'string' && (ALL_INBOUND_WEBHOOK_EVENT_TYPES as readonly string[]).includes(value);
}

/** Grupo de cada evento, para os checkboxes do editor do portal. */
export const InboundWebhookEventGroup = { LABELS: 'labels', PRODUCTS: 'products' } as const;
export type InboundWebhookEventGroup =
  (typeof InboundWebhookEventGroup)[keyof typeof InboundWebhookEventGroup];

export const INBOUND_WEBHOOK_EVENT_CATALOG: Readonly<
  Record<InboundWebhookEventType, { group: InboundWebhookEventGroup; description: string }>
> = {
  'label.consumed': {
    group: InboundWebhookEventGroup.LABELS,
    description: 'Part or all of a label was consumed in your system.',
  },
  'label.discarded': {
    group: InboundWebhookEventGroup.LABELS,
    description: 'Part or all of a label was discarded in your system.',
  },
  'label.undone': {
    group: InboundWebhookEventGroup.LABELS,
    description: 'Reverses a movement that YOUR integration recorded earlier.',
  },
  'product.created': {
    group: InboundWebhookEventGroup.PRODUCTS,
    description: 'Creates a product in the tenant catalog.',
  },
  'product.updated': {
    group: InboundWebhookEventGroup.PRODUCTS,
    description: 'Updates the fields you send (partial update).',
  },
  'product.activated': {
    group: InboundWebhookEventGroup.PRODUCTS,
    description: 'Reactivates an archived product.',
  },
  'product.deactivated': {
    group: InboundWebhookEventGroup.PRODUCTS,
    description: 'Archives a product (it stops being offered for new labels).',
  },
};

/**
 * Escopo de LEITURA exigido por evento de entrada (check D6).
 *
 * Auditoria 2026-10-04 (#803, M3): a resposta de sucesso devolve o recurso
 * inteiro (`PublicLabelDto`/`PublicProductDto`) e o log de recebidos o guarda.
 * Sem esta exigência, uma integração só com `products:read` habilitava
 * `label.consumed` e, por dry-run com `shortCode`, enumerava etiquetas sem
 * `labels:read`. Mesma regra da saída: receber/ver um recurso = ler o recurso.
 */
export const INBOUND_EVENT_REQUIRED_SCOPE: Readonly<Record<InboundWebhookEventType, ApiScope>> = {
  'label.consumed': ApiScope.LABELS_READ,
  'label.discarded': ApiScope.LABELS_READ,
  'label.undone': ApiScope.LABELS_READ,
  'product.created': ApiScope.PRODUCTS_READ,
  'product.updated': ApiScope.PRODUCTS_READ,
  'product.activated': ApiScope.PRODUCTS_READ,
  'product.deactivated': ApiScope.PRODUCTS_READ,
};

// ---------------------------------------------------------------------------
// Limites e headers
// ---------------------------------------------------------------------------

/** Origem pública da API — compõe a URL que o portal mostra ao parceiro. */
export const INBOUND_PUBLIC_API_ORIGIN = 'https://api.etiquetachef.com';
/** Prefixo da rota na API pública: `POST /v1/inbound/{endpointId}`. */
export const INBOUND_WEBHOOK_PATH = '/v1/inbound';
/** Corpo máximo aceito (A3). Um evento da v1 tem < 4 KB; 64 KB é folga de 16×. */
export const INBOUND_MAX_BODY_BYTES = 64 * 1024;
/** Janela do `webhook-timestamp` contra o relógio do servidor (B4) — a do Standard Webhooks. */
export const INBOUND_TIMESTAMP_TOLERANCE_SECONDS = 5 * 60;
/** Acima disto (dentro da janela) o relógio do parceiro merece aviso (W2). */
export const INBOUND_CLOCK_SKEW_WARN_SECONDS = 60;
/** `occurredAtMillis` aceito até este tanto no futuro (F1) — a mesma folga das rules (`now + 5 min`). */
export const INBOUND_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;
/** Evento mais velho que isto não é baixa, é reconciliação (F2). */
export const INBOUND_MAX_EVENT_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Evento mais velho que isto (e dentro dos 30 dias) leva o aviso `late_event` (W4). */
export const INBOUND_LATE_EVENT_MS = 24 * 60 * 60 * 1000;
/** Profundidade máxima do JSON (C4). O envelope mais fundo da v1 tem 4 níveis. */
export const INBOUND_MAX_JSON_DEPTH = 8;
/** Endpoints de entrada por integração. */
export const MAX_INBOUND_ENDPOINTS_PER_KEY = 5;
/** Retenção do log de recebidos e do registro de idempotência. */
export const INBOUND_EVENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Corpo guardado no log (o resto é truncado e marcado). */
export const INBOUND_LOG_BODY_MAX_BYTES = 8 * 1024;
/** Saldo abaixo desta fração do original leva o aviso `remaining_near_zero` (W6). */
export const INBOUND_REMAINING_NEAR_ZERO_RATIO = 0.01;
/** Etiqueta que vence em menos disto leva o aviso `label_expiring_soon` (W7). */
export const INBOUND_EXPIRING_SOON_MS = 24 * 60 * 60 * 1000;
/** Id do evento = `webhook-id` = id do movimento no ledger: mesmo charset do `BaixaIdSchema`. */
export const INBOUND_WEBHOOK_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
/** Id de documento aceito em `labelId`/`productId`/`supplierIds`/`tenantId`. */
export const INBOUND_DOC_ID_PATTERN = /^(?!\.\.?$)(?!__.*__$)[^/]{1,128}$/;
/** Abaixo disto o `webhook-timestamp` parece segundos plausíveis; acima, ms (B3). */
export const INBOUND_SECONDS_MAGNITUDE_LIMIT = 100_000_000_000;
/** Abaixo disto o `occurredAtMillis` parece estar em SEGUNDOS (D1). */
export const INBOUND_MILLIS_MAGNITUDE_FLOOR = 100_000_000_000;

export const InboundWebhookHeader = {
  ID: 'webhook-id',
  TIMESTAMP: 'webhook-timestamp',
  SIGNATURE: 'webhook-signature',
  IDEMPOTENCY_KEY: 'idempotency-key',
  /** `true` = roda o mapa inteiro e não grava (`result: DRY_RUN`). */
  DRY_RUN: 'etiquetachef-dry-run',
  /** Resposta: códigos de aviso separados por vírgula. */
  WARNINGS: 'etiquetachef-warnings',
  /** Resposta: `true` quando a resposta é a gravada da primeira entrega. */
  REPLAY: 'etiquetachef-idempotent-replay',
  /** Resposta: id do check que decidiu (`G10`, `I1`…). */
  CHECK: 'etiquetachef-check',
} as const;

// ---------------------------------------------------------------------------
// Códigos
// ---------------------------------------------------------------------------

export const InboundErrorCode = {
  METHOD_NOT_ALLOWED: 'method_not_allowed',
  UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
  PAYLOAD_TOO_LARGE: 'payload_too_large',
  EMPTY_BODY: 'empty_body',
  ENDPOINT_NOT_FOUND: 'endpoint_not_found',
  ENDPOINT_PAUSED: 'endpoint_paused',
  INVALID_CLIENT: 'invalid_client',
  IP_NOT_ALLOWED: 'ip_not_allowed',
  RATE_LIMITED: 'rate_limited',
  MISSING_SIGNATURE_HEADERS: 'missing_signature_headers',
  INVALID_WEBHOOK_ID: 'invalid_webhook_id',
  INVALID_TIMESTAMP: 'invalid_timestamp',
  TIMESTAMP_OUT_OF_TOLERANCE: 'timestamp_out_of_tolerance',
  INVALID_SIGNATURE_FORMAT: 'invalid_signature_format',
  INVALID_SIGNATURE: 'invalid_signature',
  IDEMPOTENCY_KEY_MISMATCH: 'idempotency_key_mismatch',
  INVALID_JSON: 'invalid_json',
  INVALID_PAYLOAD: 'invalid_payload',
  ID_MISMATCH: 'id_mismatch',
  UNSUPPORTED_EVENT_TYPE: 'unsupported_event_type',
  EVENT_NOT_ENABLED: 'event_not_enabled',
  INSUFFICIENT_SCOPE: 'insufficient_scope',
  NOT_FOUND: 'not_found',
  TENANT_UNAVAILABLE: 'tenant_unavailable',
  OCCURRED_AT_IN_FUTURE: 'occurred_at_in_future',
  OCCURRED_AT_TOO_OLD: 'occurred_at_too_old',
  OCCURRED_AFTER_SENT: 'occurred_after_sent',
  OCCURRED_BEFORE_LABEL_CREATED: 'occurred_before_label_created',
  CONSUMED_AFTER_EXPIRY: 'consumed_after_expiry',
  OCCURRED_BEFORE_MOVEMENT: 'occurred_before_movement',
  OCCURRED_BEFORE_PRODUCT_CREATED: 'occurred_before_product_created',
  STALE_EVENT: 'stale_event',
  LABEL_NOT_FOUND: 'label_not_found',
  AMBIGUOUS_REFERENCE: 'ambiguous_reference',
  LABEL_NOT_PRINTED: 'label_not_printed',
  LABEL_DELETED: 'label_deleted',
  LABEL_CLOSED: 'label_closed',
  LABEL_NOT_SUPPORTED: 'label_not_supported',
  LEDGER_FULL: 'ledger_full',
  LABEL_UNIT_UNKNOWN: 'label_unit_unknown',
  UNIT_MISMATCH: 'unit_mismatch',
  INVALID_QUANTITY_FOR_UNIT: 'invalid_quantity_for_unit',
  QUANTITY_EXCEEDS_REMAINING: 'quantity_exceeds_remaining',
  MOVEMENT_NOT_FOUND: 'movement_not_found',
  MOVEMENT_ALREADY_UNDONE: 'movement_already_undone',
  MOVEMENT_NOT_REVERSIBLE: 'movement_not_reversible',
  MOVEMENT_NOT_OWNED: 'movement_not_owned',
  PRODUCT_NOT_FOUND: 'product_not_found',
  PRODUCT_LIMIT_REACHED: 'product_limit_reached',
  DUPLICATE_PRODUCT_NAME: 'duplicate_product_name',
  SUPPLIER_NOT_FOUND: 'supplier_not_found',
  UNIT_CHANGE_BLOCKED: 'unit_change_blocked',
  IDEMPOTENCY_CONFLICT: 'idempotency_conflict',
  REQUEST_IN_PROGRESS: 'request_in_progress',
  INTERNAL: 'internal',
} as const;
export type InboundErrorCode = (typeof InboundErrorCode)[keyof typeof InboundErrorCode];
export const ALL_INBOUND_ERROR_CODES = Object.values(InboundErrorCode) as [
  InboundErrorCode,
  ...InboundErrorCode[],
];
export const InboundErrorCodeSchema = z.enum(ALL_INBOUND_ERROR_CODES);

/**
 * Status HTTP por código. A regra que o parceiro precisa memorizar:
 * 4xx = não reenviar (corrigir o dado), exceto 423 (reenviar depois de retomar
 * o endpoint) e 429; 5xx = reenviar com o MESMO `webhook-id`.
 */
export const INBOUND_ERROR_HTTP_STATUS: Readonly<Record<InboundErrorCode, number>> = {
  method_not_allowed: 405,
  unsupported_media_type: 415,
  payload_too_large: 413,
  empty_body: 400,
  endpoint_not_found: 404,
  endpoint_paused: 423,
  invalid_client: 401,
  ip_not_allowed: 403,
  rate_limited: 429,
  missing_signature_headers: 400,
  invalid_webhook_id: 400,
  invalid_timestamp: 400,
  timestamp_out_of_tolerance: 400,
  invalid_signature_format: 400,
  invalid_signature: 401,
  idempotency_key_mismatch: 400,
  invalid_json: 400,
  invalid_payload: 400,
  id_mismatch: 400,
  unsupported_event_type: 400,
  event_not_enabled: 403,
  insufficient_scope: 403,
  not_found: 404,
  tenant_unavailable: 409,
  occurred_at_in_future: 422,
  occurred_at_too_old: 422,
  occurred_after_sent: 422,
  occurred_before_label_created: 422,
  consumed_after_expiry: 422,
  occurred_before_movement: 422,
  occurred_before_product_created: 422,
  stale_event: 409,
  label_not_found: 404,
  ambiguous_reference: 409,
  label_not_printed: 409,
  label_deleted: 409,
  label_closed: 409,
  label_not_supported: 409,
  ledger_full: 409,
  label_unit_unknown: 409,
  unit_mismatch: 422,
  invalid_quantity_for_unit: 422,
  quantity_exceeds_remaining: 422,
  movement_not_found: 404,
  movement_already_undone: 409,
  movement_not_reversible: 409,
  movement_not_owned: 403,
  product_not_found: 404,
  product_limit_reached: 409,
  duplicate_product_name: 409,
  supplier_not_found: 422,
  unit_change_blocked: 409,
  idempotency_conflict: 409,
  request_in_progress: 409,
  internal: 500,
};

/** Texto de ajuda (inglês, como todo o portal). Pode mudar sem aviso — o contrato é o `code`. */
export const INBOUND_ERROR_MESSAGES: Readonly<Record<InboundErrorCode, string>> = {
  method_not_allowed: 'Only POST is accepted on an inbound webhook endpoint.',
  unsupported_media_type: 'Send the body as application/json (UTF-8).',
  payload_too_large: 'The body exceeds 64 KB.',
  empty_body: 'The body is empty.',
  endpoint_not_found: 'Unknown inbound endpoint. Check the URL shown in the developer portal.',
  endpoint_paused: 'This inbound endpoint is paused. Resume it in the developer portal and retry.',
  invalid_client: 'The integration that owns this endpoint is inactive or revoked.',
  ip_not_allowed: 'Your source IP is not in the integration allowlist.',
  rate_limited: 'Too many requests for this integration. Retry after the Retry-After seconds.',
  missing_signature_headers: 'webhook-id, webhook-timestamp and webhook-signature are required (once each).',
  invalid_webhook_id: 'webhook-id must match ^[A-Za-z0-9_-]{1,40}$.',
  invalid_timestamp: 'webhook-timestamp must be an integer number of SECONDS since the epoch.',
  timestamp_out_of_tolerance: 'webhook-timestamp is more than 5 minutes away from our clock. Sync your clock (NTP) and sign again.',
  invalid_signature_format: 'webhook-signature must be one or more space-separated "v1,<base64>" values.',
  invalid_signature: 'Signature mismatch. Sign "<webhook-id>.<webhook-timestamp>.<exact raw body>" with HMAC-SHA256 using the base64-decoded part of your whsec_ secret.',
  idempotency_key_mismatch: 'Idempotency-Key, when sent, must equal webhook-id.',
  invalid_json: 'The body is not strict JSON. See details.reason.',
  invalid_payload: 'The envelope or data.object is invalid. Every problem is listed in details.issues.',
  id_mismatch: 'The envelope "id" must equal the webhook-id header.',
  unsupported_event_type: 'This event type is not accepted on inbound webhooks.',
  event_not_enabled: 'This event type is not enabled on this endpoint. Enable it in the developer portal.',
  insufficient_scope: 'Your integration lacks the read scope for this resource (labels:read for label events, products:read for product events).',
  not_found: 'Tenant not found.',
  tenant_unavailable: 'The tenant is suspended or being deleted; it does not accept writes now.',
  occurred_at_in_future: 'occurredAtMillis is in the future (more than 5 minutes ahead of our clock).',
  occurred_at_too_old: 'occurredAtMillis is older than 30 days.',
  occurred_after_sent: 'occurredAtMillis is later than webhook-timestamp: an event cannot happen after it was sent.',
  occurred_before_label_created: 'occurredAtMillis is earlier than the label creation time.',
  consumed_after_expiry: 'A label cannot be consumed after its expiry time. Discard it instead.',
  occurred_before_movement: 'occurredAtMillis is earlier than the movement being undone.',
  occurred_before_product_created: 'occurredAtMillis is earlier than the product creation time.',
  stale_event: 'A newer event for this product was already applied. This one arrived out of order and was ignored.',
  label_not_found: 'Label not found in this tenant.',
  ambiguous_reference: 'The shortCode matches more than one open label. Send labelId instead.',
  label_not_printed: 'The label was never printed (draft); it cannot have a movement.',
  label_deleted: 'The label was deleted.',
  label_closed: 'The label is already closed (no remaining quantity). See details.status.',
  label_not_supported: 'This label predates quantity tracking and cannot receive partial movements.',
  ledger_full: 'This label reached the maximum number of movements.',
  label_unit_unknown: 'The label product no longer exists, so its unit cannot be confirmed.',
  unit_mismatch: 'The unit you sent differs from the label product unit. See details.expected.',
  invalid_quantity_for_unit: 'This unit accepts whole numbers only (decimals only for KILOGRAM and LITER).',
  quantity_exceeds_remaining: 'The quantity is greater than the label remaining quantity. See details.',
  movement_not_found: 'Movement not found on this label.',
  movement_already_undone: 'This movement was already undone.',
  movement_not_reversible: 'This movement cannot be undone (undo entries and expiries are not reversible).',
  movement_not_owned: 'Only movements recorded by this integration can be undone through the API.',
  product_not_found: 'Product not found in this tenant.',
  product_limit_reached: 'The tenant reached the maximum number of products.',
  duplicate_product_name: 'A product with this name already exists (active or archived). See details.productId.',
  supplier_not_found: 'Some supplierIds do not exist or are inactive in this tenant. See details.supplierIds.',
  unit_change_blocked: 'The unit cannot change while the product has open labels. See details.openLabels.',
  idempotency_conflict: 'This webhook-id was already used with a different body. Use a new id for a new event.',
  request_in_progress: 'A delivery with this webhook-id is being processed right now. Retry in a few seconds with the same id.',
  internal: 'Internal error. Retry with the same webhook-id.',
};

export const InboundWarningCode = {
  PREVIOUS_SECRET_USED: 'previous_secret_used',
  CLOCK_SKEW: 'clock_skew',
  IDEMPOTENCY_KEY_MISSING: 'idempotency_key_missing',
  LATE_EVENT: 'late_event',
  LABEL_CLOSED_BY_EVENT: 'label_closed_by_event',
  REMAINING_NEAR_ZERO: 'remaining_near_zero',
  LABEL_EXPIRING_SOON: 'label_expiring_soon',
  PRODUCT_HAS_OPEN_LABELS: 'product_has_open_labels',
  MISSING_EXTERNAL_REFERENCE: 'missing_external_reference',
  PRODUCT_STOCK_LOW: 'product_stock_low',
  USER_AGENT_MISSING: 'user_agent_missing',
} as const;
export type InboundWarningCode = (typeof InboundWarningCode)[keyof typeof InboundWarningCode];
export const ALL_INBOUND_WARNING_CODES = Object.values(InboundWarningCode) as [
  InboundWarningCode,
  ...InboundWarningCode[],
];

export const INBOUND_WARNING_MESSAGES: Readonly<Record<InboundWarningCode, string>> = {
  previous_secret_used: 'Signed with the PREVIOUS secret. It stops working at details.validUntilMillis — switch to the new one.',
  clock_skew: 'Your clock differs from ours by more than 60 s (details.skewSeconds). Beyond 5 minutes requests are rejected.',
  idempotency_key_missing: 'Idempotency-Key header missing; webhook-id was used. Send both for clarity.',
  late_event: 'The event happened more than 24 h ago. After 30 days it will be rejected.',
  label_closed_by_event: 'This movement used up the remaining quantity and closed the label (details.status).',
  remaining_near_zero: 'Less than 1% of the original quantity remains on this label.',
  label_expiring_soon: 'The label expires in less than 24 h.',
  product_has_open_labels: 'The product was archived but still has open labels (details.openLabels).',
  missing_external_reference: 'No sku or barcode: you will not be able to match this product with your system later.',
  product_stock_low: 'The product stock fell below its minimum (details).',
  user_agent_missing: 'Send a User-Agent identifying your system; it speeds up support.',
};

/** Problema de formato dentro de `invalid_payload` (`details.issues[].issue`). */
export const InboundPayloadIssue = {
  MISSING_FIELD: 'missing_field',
  UNKNOWN_FIELD: 'unknown_field',
  INVALID_TYPE: 'invalid_type',
  INVALID_FORMAT: 'invalid_format',
  INVALID_ENUM: 'invalid_enum',
  TOO_SHORT: 'too_short',
  TOO_LONG: 'too_long',
  OUT_OF_RANGE: 'out_of_range',
  TOO_MANY_DECIMALS: 'too_many_decimals',
  NOT_INTEGER: 'not_integer',
  DUPLICATE_ITEM: 'duplicate_item',
  EXACTLY_ONE_REQUIRED: 'exactly_one_required',
  CONTROL_CHARACTERS: 'control_characters',
  INVALID_CHECK_DIGIT: 'invalid_check_digit',
  EMPTY_UPDATE: 'empty_update',
  SECONDS_INSTEAD_OF_MILLIS: 'seconds_instead_of_millis',
} as const;
export type InboundPayloadIssue = (typeof InboundPayloadIssue)[keyof typeof InboundPayloadIssue];
export const ALL_INBOUND_PAYLOAD_ISSUES = Object.values(InboundPayloadIssue) as [
  InboundPayloadIssue,
  ...InboundPayloadIssue[],
];

/** Motivo dentro de `invalid_json` (`details.reason`). */
export const InboundJsonReason = {
  INVALID_UTF8: 'invalid_utf8',
  MALFORMED: 'malformed',
  NOT_OBJECT: 'not_object',
  DUPLICATE_KEY: 'duplicate_key',
  TOO_DEEP: 'too_deep',
  FORBIDDEN_KEY: 'forbidden_key',
  NON_FINITE_NUMBER: 'non_finite_number',
} as const;
export type InboundJsonReason = (typeof InboundJsonReason)[keyof typeof InboundJsonReason];

// ---------------------------------------------------------------------------
// Mapa de checagem
// ---------------------------------------------------------------------------

export const InboundCheckPhase = {
  TRANSPORT: 'A',
  SIGNATURE: 'B',
  JSON: 'C',
  PAYLOAD: 'D',
  REACH: 'E',
  TIME: 'F',
  DOMAIN: 'G',
  IDEMPOTENCY: 'H',
  ACCEPT: 'I',
  WARNING: 'W',
} as const;
export type InboundCheckPhase = (typeof InboundCheckPhase)[keyof typeof InboundCheckPhase];

export const INBOUND_CHECK_PHASE_TITLES: Readonly<Record<InboundCheckPhase, string>> = {
  A: 'Transport',
  B: 'Signature',
  C: 'JSON body',
  D: 'Envelope & payload',
  E: 'Tenant reach',
  F: 'Time',
  G: 'Business rules',
  H: 'Idempotency',
  I: 'Accepted',
  W: 'Warnings (accepted)',
};

/** Resultado de uma mensagem aceita. */
export const InboundResult = {
  /** Gravado agora. */
  APPLIED: 'APPLIED',
  /** Mesmo `webhook-id` e mesmo corpo de uma entrega anterior: devolve a resposta dela. */
  REPLAYED: 'REPLAYED',
  /** Header de dry-run: o mapa inteiro passou, nada foi gravado. */
  DRY_RUN: 'DRY_RUN',
  /** O estado já era o pedido (ativar produto ativo). */
  NO_CHANGE: 'NO_CHANGE',
} as const;
export type InboundResult = (typeof InboundResult)[keyof typeof InboundResult];

export type InboundCheckOutcome =
  | { kind: 'error'; code: InboundErrorCode }
  | { kind: 'warning'; code: InboundWarningCode }
  | { kind: 'success'; result: InboundResult };

export type InboundCheck = {
  /** `A1`…`W11`. Estável — aparece no header `EtiquetaChef-Check` e no log. */
  id: string;
  phase: InboundCheckPhase;
  /** O que é verificado (inglês, como o portal). */
  check: string;
  outcome: InboundCheckOutcome;
  /** O que o parceiro deve fazer quando cai aqui. */
  howToHandle: string;
  /** Eventos a que o check se aplica; ausente = todos. */
  appliesTo?: readonly InboundWebhookEventType[];
};

const LABEL_MOVES = ['label.consumed', 'label.discarded'] as const;
const LABEL_ALL = ['label.consumed', 'label.discarded', 'label.undone'] as const;
const PRODUCT_BY_ID = ['product.updated', 'product.activated', 'product.deactivated'] as const;

const err = (code: InboundErrorCode): InboundCheckOutcome => ({ kind: 'error', code });
const warn = (code: InboundWarningCode): InboundCheckOutcome => ({ kind: 'warning', code });
const ok = (result: InboundResult): InboundCheckOutcome => ({ kind: 'success', result });

/**
 * O mapa de checagem — TUDO que é verificado antes de aceitar uma mensagem do
 * parceiro, na ordem em que o pipeline executa.
 *
 * Origem: #803 — 2026-10-04 14:48 +0200 (owner: "crie um mapeamento de
 * checagem antes de aceitar uma mensagem webhook vinda do parceiro"; "mapear
 * todas as possibilidades").
 *
 * Por quê: a primeira porta de escrita externa precisa recusar por padrão. Ter
 * o mapa como DADO (e não só como código) é o que permite à bancada do portal
 * oferecer um teste por linha, à doc listar os mesmos códigos, e ao gate de
 * cobertura reprovar check novo sem cenário.
 *
 * Contrato: ordem do array = ordem de execução. A primeira falha encerra a
 * requisição, exceto na fase D, que acumula todos os problemas de formato em
 * `details.issues`. A FASE é o rótulo do tipo de checagem, não a posição:
 * os checks de tempo da entidade (F4–F8) só podem rodar depois de a entidade
 * ser localizada (G1/G16), e a idempotência (H) roda antes do domínio para a
 * reentrega devolver a resposta original mesmo que o estado tenha mudado. Os
 * avisos (W) nunca bloqueiam.
 *
 * Usado por: `webhooks/inbound/process.ts` (trace e header `EtiquetaChef-Check`),
 * `webhooks/inbound/scenarios.ts` (um cenário por linha, no mínimo), portal
 * (`InboundChecksTable`, `InboundTestBench`) e a doc gerada do portal.
 *
 * Dicas: mudar a ordem muda a resposta que o parceiro recebe para uma mensagem
 * com dois problemas — os cenários com dois defeitos no teste de cobertura
 * fixam a ordem. Nunca ler dado do tenant antes da fase B passar.
 */
export const INBOUND_CHECKS: readonly InboundCheck[] = [
  // A — transporte
  { id: 'A1', phase: 'A', check: 'HTTP method is POST', outcome: err('method_not_allowed'), howToHandle: 'Use POST. Do not retry other methods.' },
  { id: 'A2', phase: 'A', check: 'Content-Type is application/json (charset absent or utf-8)', outcome: err('unsupported_media_type'), howToHandle: 'Set "Content-Type: application/json". Note: a charset other than UTF-8 is refused by our HTTP layer before this map runs (plain-text 415, not JSON). Do not retry unchanged.' },
  { id: 'A3', phase: 'A', check: 'Body is at most 64 KB', outcome: err('payload_too_large'), howToHandle: 'Send one event per request; v1 events are well below the limit.' },
  { id: 'A4', phase: 'A', check: 'Body is not empty', outcome: err('empty_body'), howToHandle: 'Send the JSON envelope as the body.' },
  { id: 'A5', phase: 'A', check: 'The endpoint id in the URL exists', outcome: err('endpoint_not_found'), howToHandle: 'Copy the URL from the developer portal; endpoints deleted there stop existing.' },
  { id: 'A7', phase: 'A', check: 'The integration (API key) that owns the endpoint is active (checked before the pause: a revoked integration answers 401, not 423)', outcome: err('invalid_client'), howToHandle: 'Contact EtiquetaChef support: the integration was revoked.' },
  { id: 'A6', phase: 'A', check: 'The endpoint is ACTIVE', outcome: err('endpoint_paused'), howToHandle: 'Queue the event, resume the endpoint in the portal, then retry with the same webhook-id.' },
  { id: 'A8', phase: 'A', check: 'Source IP is in the integration allowlist (when one is set)', outcome: err('ip_not_allowed'), howToHandle: 'Send from an allowlisted IP or ask support to add yours.' },
  // B — assinatura
  { id: 'B1', phase: 'B', check: 'webhook-id, webhook-timestamp and webhook-signature are present exactly once', outcome: err('missing_signature_headers'), howToHandle: 'Send the three Standard Webhooks headers.' },
  { id: 'B2', phase: 'B', check: 'webhook-id matches ^[A-Za-z0-9_-]{1,40}$', outcome: err('invalid_webhook_id'), howToHandle: 'Generate ids with letters, digits, "_" and "-" only, up to 40 characters.' },
  { id: 'B3', phase: 'B', check: 'webhook-timestamp is an integer in SECONDS', outcome: err('invalid_timestamp'), howToHandle: 'Use Math.floor(Date.now() / 1000), not milliseconds.' },
  { id: 'B4', phase: 'B', check: 'webhook-timestamp is within ±5 minutes of our clock', outcome: err('timestamp_out_of_tolerance'), howToHandle: 'Sync your clock (NTP) and sign again with a fresh timestamp. Do not reuse old signed requests.' },
  { id: 'B5', phase: 'B', check: 'webhook-signature has the form "v1,<base64>" (space-separated list allowed)', outcome: err('invalid_signature_format'), howToHandle: 'Prefix each signature with "v1,".' },
  { id: 'B6', phase: 'B', check: 'HMAC-SHA256 of "id.timestamp.rawBody" matches the current secret (or the previous one during the 24 h after a rotation), compared in constant time', outcome: err('invalid_signature'), howToHandle: 'Sign the EXACT bytes you send (no re-serialization after signing) with the base64-decoded part after "whsec_".' },
  { id: 'B7', phase: 'B', check: 'Idempotency-Key, when present, equals webhook-id', outcome: err('idempotency_key_mismatch'), howToHandle: 'Send the same value in both headers.' },
  // A9 depois da assinatura (auditoria #803, M4): quem só conhece a URL não
  // gasta a cota da integração (que é compartilhada com a API REST de leitura).
  { id: 'A9', phase: 'A', check: 'Integration rate limit (requests per minute) — counted only after the signature is valid', outcome: err('rate_limited'), howToHandle: 'Wait Retry-After seconds and retry with the same webhook-id.' },
  // C — JSON
  { id: 'C1', phase: 'C', check: 'Body is valid UTF-8', outcome: err('invalid_json'), howToHandle: 'Encode the body as UTF-8.' },
  // Limitação de plataforma (#803, medido 2026-10-04 no functions-framework e
  // no emulador): o `body-parser.json` do runtime faz o parse ANTES da função e
  // responde ele mesmo `400` (corpo de texto, sem o nosso envelope e sem linha
  // no log) para JSON malformado e para raiz que não é objeto nem array. Só o
  // array chega até nós como `invalid_json`. Não há opção em `onRequest` v2
  // para desligar o parser — por isso o "como tratar" ensina a reconhecer o 400
  // sem JSON.
  { id: 'C2', phase: 'C', check: 'Body is well-formed JSON whose root is an object', outcome: err('invalid_json'), howToHandle: 'Serialize the envelope with a JSON library. Note: syntactically broken JSON is rejected by our HTTP layer before this map runs — you get a plain 400 without a JSON body (and it does not appear in the Received log). Treat any 400 whose body is not JSON as "malformed JSON".' },
  { id: 'C3', phase: 'C', check: 'No duplicated keys in any object', outcome: err('invalid_json'), howToHandle: 'Remove the duplicated key; we never pick one of them for you.' },
  { id: 'C4', phase: 'C', check: 'Nesting depth ≤ 8 and no "__proto__", "constructor" or "prototype" keys', outcome: err('invalid_json'), howToHandle: 'Send only the documented fields.' },
  { id: 'C5', phase: 'C', check: 'All numbers are finite (e.g. 1e309 is rejected)', outcome: err('invalid_json'), howToHandle: 'Send real quantities.' },
  // D — envelope e objeto
  { id: 'D1', phase: 'D', check: 'Envelope has exactly id, type, apiVersion ("v1"), occurredAtMillis (integer ms), tenantId and data.object — all problems are listed at once', outcome: err('invalid_payload'), howToHandle: 'Fix every entry in details.issues; do not retry unchanged.' },
  { id: 'D2', phase: 'D', check: 'Envelope "id" equals the webhook-id header', outcome: err('id_mismatch'), howToHandle: 'Use the same id in the header and in the body.' },
  { id: 'D3', phase: 'D', check: 'type is one of the inbound event types', outcome: err('unsupported_event_type'), howToHandle: 'Only the documented inbound events are accepted.' },
  { id: 'D4', phase: 'D', check: 'type is enabled on this endpoint', outcome: err('event_not_enabled'), howToHandle: 'Enable the event on the endpoint in the developer portal.' },
  { id: 'D6', phase: 'D', check: 'The integration has the READ scope of the resource (labels:read / products:read) — the response returns the resource', outcome: err('insufficient_scope'), howToHandle: 'Ask your EtiquetaChef contact to add the read scope to the integration.' },
  { id: 'D5', phase: 'D', check: 'data.object matches the strict schema of the event (required fields, types, ranges, formats, enums, no unknown fields, no control characters)', outcome: err('invalid_payload'), howToHandle: 'Fix every entry in details.issues.' },
  // E — alcance
  { id: 'E1', phase: 'E', check: 'Tenant exists and is within the integration reach', outcome: err('not_found'), howToHandle: 'Check the tenantId; tenants outside your reach look exactly like missing ones.' },
  { id: 'E2', phase: 'E', check: 'Tenant is not suspended', outcome: err('tenant_unavailable'), howToHandle: 'Retry later; the tenant is not accepting writes.' },
  // F — tempo (relógio do servidor)
  { id: 'F1', phase: 'F', check: 'occurredAtMillis ≤ now + 5 min', outcome: err('occurred_at_in_future'), howToHandle: 'Send the time the event happened, in milliseconds, from a synced clock.' },
  { id: 'F2', phase: 'F', check: 'occurredAtMillis ≥ now − 30 days', outcome: err('occurred_at_too_old'), howToHandle: 'Events older than 30 days must be reconciled with support.' },
  { id: 'F3', phase: 'F', check: 'occurredAtMillis ≤ webhook-timestamp + 5 min', outcome: err('occurred_after_sent'), howToHandle: 'occurredAtMillis is when it happened; it cannot be after you sent it.' },
  // H — idempotência: ANTES do domínio — a reentrega devolve a resposta original
  // mesmo que o estado tenha mudado desde então
  { id: 'H1', phase: 'H', check: 'Same webhook-id AND same body as an earlier delivery → the original response is returned', outcome: ok('REPLAYED'), howToHandle: 'Safe: retries never apply twice.' },
  { id: 'H2', phase: 'H', check: 'Same webhook-id with a DIFFERENT body', outcome: err('idempotency_conflict'), howToHandle: 'Generate a new webhook-id for every new event.' },
  { id: 'H3', phase: 'H', check: 'Same webhook-id is being processed by another delivery right now', outcome: err('request_in_progress'), howToHandle: 'Retry in a few seconds with the same webhook-id.' },
  // G/F — etiqueta: localizar, estado, tempo da etiqueta, unidade e saldo
  { id: 'G1', phase: 'G', check: 'Label exists in the tenant', outcome: err('label_not_found'), howToHandle: 'Check labelId/shortCode and tenantId.', appliesTo: LABEL_ALL },
  { id: 'G2', phase: 'G', check: 'shortCode resolves to exactly one open label', outcome: err('ambiguous_reference'), howToHandle: 'Send labelId (from our outgoing webhooks or GET /labels).', appliesTo: LABEL_MOVES },
  { id: 'G3', phase: 'G', check: 'Label is not a draft (it was printed)', outcome: err('label_not_printed'), howToHandle: 'Only printed labels have stock to move.', appliesTo: LABEL_ALL },
  { id: 'G4', phase: 'G', check: 'Label is not deleted', outcome: err('label_deleted'), howToHandle: 'Do not retry; the label no longer exists for stock.', appliesTo: LABEL_ALL },
  { id: 'G5', phase: 'G', check: 'Label is open (not CONSUMED, DISCARDED, EXPIRED or MIXED)', outcome: err('label_closed'), howToHandle: 'Do not retry; read details.status and update your system.', appliesTo: LABEL_MOVES },
  { id: 'G6', phase: 'G', check: 'Label supports quantity movements (ledger)', outcome: err('label_not_supported'), howToHandle: 'Close this label in the EtiquetaChef app.', appliesTo: LABEL_ALL },
  { id: 'G7', phase: 'G', check: 'Label ledger is below 500 movements', outcome: err('ledger_full'), howToHandle: 'Group movements; close the label in the app.', appliesTo: LABEL_ALL },
  { id: 'F4', phase: 'F', check: 'Movement: occurredAtMillis ≥ label creation', outcome: err('occurred_before_label_created'), howToHandle: 'Check the label id and the event time.', appliesTo: LABEL_MOVES },
  { id: 'F5', phase: 'F', check: 'Consumption: occurredAtMillis < label expiry (food safety)', outcome: err('consumed_after_expiry'), howToHandle: 'Food past its expiry cannot be consumed; send label.discarded.', appliesTo: ['label.consumed'] },
  { id: 'G8', phase: 'G', check: 'The label product still exists (its unit is known)', outcome: err('label_unit_unknown'), howToHandle: 'Close this label in the EtiquetaChef app.', appliesTo: LABEL_MOVES },
  { id: 'G9', phase: 'G', check: 'unit equals the label product unit', outcome: err('unit_mismatch'), howToHandle: 'Convert to details.expected before sending (e.g. 1500 GRAM → 1.5 KILOGRAM).', appliesTo: LABEL_MOVES },
  { id: 'G10', phase: 'G', check: 'quantity is valid for the unit (whole numbers except KILOGRAM/LITER)', outcome: err('invalid_quantity_for_unit'), howToHandle: 'Round to whole units or use KILOGRAM/LITER products.', appliesTo: LABEL_MOVES },
  { id: 'G11', phase: 'G', check: 'quantity ≤ remaining quantity of the label', outcome: err('quantity_exceeds_remaining'), howToHandle: 'Do not retry: details.remaining is what is left. Reconcile your stock and send at most that.', appliesTo: LABEL_MOVES },
  // G/F — desfazer movimento
  { id: 'G12', phase: 'G', check: 'Undo: the movement exists on the label', outcome: err('movement_not_found'), howToHandle: 'Use the movement.id returned when you recorded it.', appliesTo: ['label.undone'] },
  { id: 'G13', phase: 'G', check: 'Undo: the movement was not undone yet', outcome: err('movement_already_undone'), howToHandle: 'Nothing to do.', appliesTo: ['label.undone'] },
  { id: 'G14', phase: 'G', check: 'Undo: the movement is reversible (not an undo, not an expiry)', outcome: err('movement_not_reversible'), howToHandle: 'Expiries are recorded by EtiquetaChef only.', appliesTo: ['label.undone'] },
  { id: 'G15', phase: 'G', check: 'Undo: the movement was recorded by THIS integration', outcome: err('movement_not_owned'), howToHandle: 'Movements made by people or other integrations are undone in the app.', appliesTo: ['label.undone'] },
  { id: 'F6', phase: 'F', check: 'Undo: occurredAtMillis ≥ the undone movement time', outcome: err('occurred_before_movement'), howToHandle: 'Undo can only happen after the movement.', appliesTo: ['label.undone'] },
  // G/F — produto
  { id: 'G16', phase: 'G', check: 'Product exists in the tenant', outcome: err('product_not_found'), howToHandle: 'Use the id returned by product.created or GET /products.', appliesTo: PRODUCT_BY_ID },
  { id: 'F7', phase: 'F', check: 'Product: occurredAtMillis ≥ product creation', outcome: err('occurred_before_product_created'), howToHandle: 'Check the product id and the event time.', appliesTo: PRODUCT_BY_ID },
  { id: 'F8', phase: 'F', check: 'Product: occurredAtMillis is newer than the last applied event for that product (out-of-order protection)', outcome: err('stale_event'), howToHandle: 'Nothing to do: a newer state already won. Do not retry.', appliesTo: PRODUCT_BY_ID },
  { id: 'G17', phase: 'G', check: 'Create: the tenant is below the product limit', outcome: err('product_limit_reached'), howToHandle: 'Archive unused products in the app.', appliesTo: ['product.created'] },
  { id: 'G18', phase: 'G', check: 'Create/rename: name is unique in the catalog (case and accent insensitive, archived included)', outcome: err('duplicate_product_name'), howToHandle: 'Update details.productId instead of creating another one.', appliesTo: ['product.created', 'product.updated'] },
  { id: 'G19', phase: 'G', check: 'supplierIds exist and are active in the tenant', outcome: err('supplier_not_found'), howToHandle: 'Send ids from GET suppliers / supplier webhooks.', appliesTo: ['product.created', 'product.updated'] },
  { id: 'G20', phase: 'G', check: 'Update: unit does not change while the product has open labels', outcome: err('unit_change_blocked'), howToHandle: 'Close the open labels first, or create a new product with the new unit.', appliesTo: ['product.updated'] },
  // I — aceite
  { id: 'I1', phase: 'I', check: 'All checks passed — the event was applied', outcome: ok('APPLIED'), howToHandle: 'Store data.movement.id (labels) or data.object.id (products).' },
  { id: 'I2', phase: 'I', check: 'Dry-run header — all checks passed, nothing was written', outcome: ok('DRY_RUN'), howToHandle: 'Remove the header to apply for real.' },
  { id: 'I3', phase: 'I', check: 'The product already was in the requested state', outcome: ok('NO_CHANGE'), howToHandle: 'Nothing to do.', appliesTo: ['product.activated', 'product.deactivated'] },
  // W — avisos (aceita e avisa)
  { id: 'W1', phase: 'W', check: 'Signed with the previous secret (rotation grace window)', outcome: warn('previous_secret_used'), howToHandle: 'Switch to the new secret before details.validUntilMillis.' },
  { id: 'W2', phase: 'W', check: 'Clock skew above 60 s', outcome: warn('clock_skew'), howToHandle: 'Enable NTP on the sender.' },
  { id: 'W3', phase: 'W', check: 'Idempotency-Key header missing', outcome: warn('idempotency_key_missing'), howToHandle: 'Send Idempotency-Key equal to webhook-id.' },
  { id: 'W4', phase: 'W', check: 'Event older than 24 h', outcome: warn('late_event'), howToHandle: 'Deliver sooner; after 30 days it is rejected.' },
  { id: 'W5', phase: 'W', check: 'The movement closed the label', outcome: warn('label_closed_by_event'), howToHandle: 'Mark the label closed in your system.', appliesTo: LABEL_MOVES },
  { id: 'W6', phase: 'W', check: 'Remaining quantity below 1% of the original', outcome: warn('remaining_near_zero'), howToHandle: 'Consider closing the label with the remaining quantity.', appliesTo: LABEL_MOVES },
  { id: 'W7', phase: 'W', check: 'Label expires in less than 24 h', outcome: warn('label_expiring_soon'), howToHandle: 'Use it first or plan a discard.', appliesTo: LABEL_MOVES },
  { id: 'W8', phase: 'W', check: 'Archived product still has open labels', outcome: warn('product_has_open_labels'), howToHandle: 'Close or move those labels.', appliesTo: ['product.deactivated'] },
  { id: 'W9', phase: 'W', check: 'Product without sku and barcode', outcome: warn('missing_external_reference'), howToHandle: 'Send your code in sku so both systems can match the product.', appliesTo: ['product.created', 'product.updated'] },
  { id: 'W10', phase: 'W', check: 'Product stock fell below its minimum', outcome: warn('product_stock_low'), howToHandle: 'Plan replenishment.', appliesTo: LABEL_MOVES },
  { id: 'W11', phase: 'W', check: 'User-Agent header missing', outcome: warn('user_agent_missing'), howToHandle: 'Send "User-Agent: <your-system>/<version>".' },
];

/** Check pelo id — lança se não existir (erro de programação, nunca de entrada). */
export function inboundCheck(id: string): InboundCheck {
  const found = INBOUND_CHECKS.find((c) => c.id === id);
  if (found === undefined) throw new Error(`inbound_check_unknown:${id}`);
  return found;
}

// ---------------------------------------------------------------------------
// Schemas estritos
// ---------------------------------------------------------------------------

/** Controle C0, DEL e overrides bidi — invisíveis na tela e perigosos em log/etiqueta. */
const CONTROL_OR_BIDI = /[\u0000-\u001F\u007F‪-‮⁦-⁩]/;

/**
 * Texto do parceiro: NFC, `trim`, sem caractere de controle/bidi, tamanho medido
 * DEPOIS do trim (string só de espaços = ausente → `too_short`).
 */
function inboundText(min: number, max: number): z.ZodString {
  return z
    .string()
    .normalize('NFC')
    .trim()
    .refine((s) => !CONTROL_OR_BIDI.test(s), {
      message: 'Control or bidirectional characters are not allowed.',
      params: { issue: InboundPayloadIssue.CONTROL_CHARACTERS },
    })
    .min(min)
    .max(max);
}

const DocIdSchema = z.string().regex(INBOUND_DOC_ID_PATTERN, 'Invalid id.');

/** Quantidade: número finito, > 0, ≤ QUANTITY_MAX, ≤ 3 casas — a unidade é checada no domínio (G10). */
const QuantitySchema = z
  .number()
  .gt(0)
  .max(QUANTITY_MAX)
  .refine((v) => Number(v.toFixed(QUANTITY_MAX_DECIMALS)) === v, {
    message: `At most ${String(QUANTITY_MAX_DECIMALS)} decimal places.`,
    params: { issue: InboundPayloadIssue.TOO_MANY_DECIMALS },
  });

/** Lista sem item repetido (comparação exata depois da normalização do item). */
function uniqueArray<T extends z.ZodType>(item: T, max: number): z.ZodArray<T> {
  return z
    .array(item)
    .max(max)
    .refine((arr) => new Set(arr.map((v) => JSON.stringify(v))).size === arr.length, {
      message: 'Duplicated items.',
      params: { issue: InboundPayloadIssue.DUPLICATE_ITEM },
    });
}

/**
 * Dígito verificador GS1 (EAN-8, UPC-A, EAN-13, GTIN-14). Só é exigido quando o
 * código é inteiramente numérico com um desses tamanhos: um código interno
 * alfanumérico (`ABC-123`) não tem dígito verificador a conferir.
 *
 * Origem: #803 — 2026-10-04 15:35 +0200 (commit `f249444dc`).
 *
 * Contrato: `true` para código não-GS1 (não numérico ou fora de 8/12/13/14
 * dígitos) e para GS1 com dígito correto (módulo 10, pesos 3/1 da direita).
 *
 * Usado por: o `refine` do `barcode` nos schemas de produto de entrada (este
 * arquivo) — reprovação vira issue de payload no pipeline (`payload.ts`).
 *
 * Histórico:
 *  - 2026-10-10 14:34 +0200 — (#799) cabeçalho padrão (#796).
 */
export function isValidGs1CheckDigit(code: string): boolean {
  if (!/^\d+$/.test(code) || ![8, 12, 13, 14].includes(code.length)) return true;
  const digits = code.split('').map(Number);
  const check = digits.pop() ?? 0;
  const sum = digits
    .reverse()
    .reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

const BarcodeSchema = z
  .string()
  .trim()
  .max(60)
  .regex(/^[0-9A-Za-z-]*$/, 'Only letters, digits and "-".')
  .refine(isValidGs1CheckDigit, {
    message: 'Invalid GS1 check digit.',
    params: { issue: InboundPayloadIssue.INVALID_CHECK_DIGIT },
  });

const ValidityRuleSchema = z.strictObject({
  name: inboundText(1, 100),
  durationHours: z.number().int().min(1).max(MAX_VALIDITY_DURATION_HOURS),
  isDefault: z.boolean(),
});

const ValidityRulesSchema = z
  .array(ValidityRuleSchema)
  .min(1)
  .max(20)
  .superRefine((rules, ctx) => {
    const defaults = rules.filter((r) => r.isDefault).length;
    if (defaults !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: `Exactly one rule must have isDefault = true (got ${String(defaults)}).`,
        params: { issue: InboundPayloadIssue.EXACTLY_ONE_REQUIRED },
      });
    }
    const names = rules.map((r) => r.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Rule names must be unique.',
        params: { issue: InboundPayloadIssue.DUPLICATE_ITEM },
      });
    }
  });

/** Campos de produto aceitos na entrada (os do formulário do app; nada de imagem, id, tenant ou `active`). */
const productFieldShape = {
  name: inboundText(1, 120),
  description: inboundText(0, 1000).optional(),
  category: z.enum(ALL_PRODUCT_CATEGORIES),
  unit: z.enum(ALL_UNITS_OF_MEASURE),
  sku: inboundText(0, 60).optional(),
  barcode: BarcodeSchema.nullable().optional(),
  ingredients: uniqueArray(inboundText(1, 120), 80).optional(),
  allergens: uniqueArray(z.enum(ALL_ALLERGENS), ALL_ALLERGENS.length).optional(),
  validityRules: ValidityRulesSchema,
  supplierIds: uniqueArray(DocIdSchema, 20).optional(),
  minStock: z.number().int().min(0).max(1_000_000).nullable().optional(),
};

export const InboundLabelMovementObjectSchema = z
  .strictObject({
    labelId: DocIdSchema.optional(),
    shortCode: z.string().regex(/^\d{6}$/, 'shortCode is a string of 6 digits.').optional(),
    quantity: QuantitySchema,
    unit: z.enum(ALL_UNITS_OF_MEASURE),
  })
  .superRefine((obj, ctx) => {
    if ((obj.labelId === undefined) === (obj.shortCode === undefined)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Send exactly one of labelId or shortCode.',
        path: ['labelId'],
        params: { issue: InboundPayloadIssue.EXACTLY_ONE_REQUIRED },
      });
    }
  });
export type InboundLabelMovementObject = z.infer<typeof InboundLabelMovementObjectSchema>;

export const InboundLabelUndoObjectSchema = z.strictObject({
  labelId: DocIdSchema,
  movementId: z.string().regex(INBOUND_WEBHOOK_ID_PATTERN, 'Invalid movement id.'),
});
export type InboundLabelUndoObject = z.infer<typeof InboundLabelUndoObjectSchema>;

export const InboundProductCreateObjectSchema = z.strictObject(productFieldShape);
export type InboundProductCreateObject = z.infer<typeof InboundProductCreateObjectSchema>;

export const InboundProductUpdateObjectSchema = z.strictObject({
  productId: DocIdSchema,
  fields: z
    .strictObject({
      name: productFieldShape.name.optional(),
      description: productFieldShape.description,
      category: productFieldShape.category.optional(),
      unit: productFieldShape.unit.optional(),
      sku: productFieldShape.sku,
      barcode: productFieldShape.barcode,
      ingredients: productFieldShape.ingredients,
      allergens: productFieldShape.allergens,
      validityRules: productFieldShape.validityRules.optional(),
      supplierIds: productFieldShape.supplierIds,
    })
    .refine((f) => Object.keys(f).length > 0, {
      message: 'fields is empty: send at least one field to change.',
      params: { issue: InboundPayloadIssue.EMPTY_UPDATE },
    }),
});
export type InboundProductUpdateObject = z.infer<typeof InboundProductUpdateObjectSchema>;

export const InboundProductRefObjectSchema = z.strictObject({ productId: DocIdSchema });
export type InboundProductRefObject = z.infer<typeof InboundProductRefObjectSchema>;

/** Schema do `data.object` por evento. */
export const INBOUND_OBJECT_SCHEMAS: Readonly<Record<InboundWebhookEventType, z.ZodType>> = {
  'label.consumed': InboundLabelMovementObjectSchema,
  'label.discarded': InboundLabelMovementObjectSchema,
  'label.undone': InboundLabelUndoObjectSchema,
  'product.created': InboundProductCreateObjectSchema,
  'product.updated': InboundProductUpdateObjectSchema,
  'product.activated': InboundProductRefObjectSchema,
  'product.deactivated': InboundProductRefObjectSchema,
};

/**
 * Envelope — o MESMO do webhook de saída (`WebhookEnvelopeSchema`), mas
 * estrito e com `type` livre aqui: o tipo desconhecido tem código próprio
 * (`unsupported_event_type`, D3), e `data.object` é validado à parte (D5)
 * para os problemas dos dois níveis saírem juntos.
 */
export const InboundEnvelopeSchema = z.strictObject({
  id: z.string().regex(INBOUND_WEBHOOK_ID_PATTERN, 'Invalid id.'),
  type: z.string().min(1).max(64),
  apiVersion: z.literal(WEBHOOK_API_VERSION),
  occurredAtMillis: z
    .number()
    .int()
    .nonnegative()
    .refine((v) => v >= INBOUND_MILLIS_MAGNITUDE_FLOOR, {
      message: 'occurredAtMillis looks like SECONDS; send milliseconds.',
      params: { issue: InboundPayloadIssue.SECONDS_INSTEAD_OF_MILLIS },
    }),
  tenantId: DocIdSchema,
  data: z.strictObject({ object: z.record(z.string(), z.unknown()) }),
});
export type InboundEnvelope = z.infer<typeof InboundEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Respostas
// ---------------------------------------------------------------------------

export type InboundIssueDetail = {
  /** Caminho no corpo, ex. `data.object.quantity`. */
  path: string;
  issue: InboundPayloadIssue;
  message: string;
  expected?: string;
  received?: string;
};

export type InboundWarning = {
  code: InboundWarningCode;
  checkId: string;
  message: string;
  details?: Record<string, unknown>;
};

export type InboundSuccessBody = {
  data: {
    result: InboundResult;
    eventId: string;
    type: InboundWebhookEventType;
    /** DTO público (`PublicLabelDto`/`PublicProductDto`) depois da mudança. */
    object: unknown;
    /** Só em baixa/undo: o movimento gravado (guarde o `id` para um undo). */
    movement?: { id: string; kind: string; quantity: number; atMillis: number } | null;
    warnings: InboundWarning[];
  };
};

export type InboundErrorBody = {
  error: {
    code: InboundErrorCode;
    message: string;
    /** Id do check que reprovou (`G11`…). */
    checkId: string;
    details?: Record<string, unknown>;
  };
};

/** Um passo do mapa executado — a bancada mostra a lista com ✓/✗. */
export type InboundTraceStep = {
  checkId: string;
  ok: boolean;
  note?: string;
};

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------

export const InboundCollection = {
  ENDPOINTS: 'inbound_webhook_endpoints',
  /** Log de recebidos (portal), um doc por requisição, TTL 30 dias. */
  EVENTS: 'inbound_webhook_events',
  /**
   * Registro de idempotência: `{endpointId}__{webhookId}` → reserva/resposta
   * final (H1–H3), TTL 30 dias. Separado do log porque o log tem um doc por
   * ENTREGA (inclusive as recusadas antes de haver id confiável) e o registro
   * tem um por EVENTO.
   */
  RECEIPTS: 'inbound_webhook_receipts',
} as const;

export const InboundEndpointStatus = { ACTIVE: 'ACTIVE', PAUSED: 'PAUSED' } as const;
export type InboundEndpointStatus = (typeof InboundEndpointStatus)[keyof typeof InboundEndpointStatus];
export const InboundEndpointStatusSchema = z.enum([InboundEndpointStatus.ACTIVE, InboundEndpointStatus.PAUSED]);

/**
 * `inbound_webhook_endpoints/{endpointId}` — só o servidor lê/escreve (rules
 * negam o cliente). O segredo NÃO é guardado: é derivado de
 * `WEBHOOK_SIGNING_MASTER` com o namespace `inbound:<id>:<versão>`.
 */
export const InboundEndpointSchema = z.object({
  id: z.string().min(1),
  keyId: z.string().min(1),
  description: z.string().max(200).default(''),
  events: z.array(InboundWebhookEventTypeSchema).min(1),
  status: InboundEndpointStatusSchema,
  secretVersion: z.number().int().min(1),
  previousSecretValidUntilMillis: z.number().int().nullable().default(null),
  createdByEmail: z.string().default(''),
  createdAtMillis: z.number().int(),
  updatedAtMillis: z.number().int(),
  lastReceivedAtMillis: z.number().int().nullable().default(null),
});
export type InboundEndpoint = z.infer<typeof InboundEndpointSchema>;

/**
 * `inbound_webhook_events/{autoId}` — uma linha por ENTREGA recebida, aceita
 * ou recusada em qualquer fase, para a aba "Received" do portal. TTL de 30
 * dias (`expireAt`). A idempotência NÃO mora aqui (ver
 * `InboundCollection.RECEIPTS`).
 */
export const InboundEventLogSchema = z.object({
  endpointId: z.string(),
  keyId: z.string(),
  webhookId: z.string().nullable(),
  type: z.string().nullable(),
  tenantId: z.string().nullable(),
  receivedAtMillis: z.number().int(),
  status: z.number().int(),
  result: z.string().nullable(),
  code: z.string().nullable(),
  checkId: z.string(),
  warnings: z.array(z.string()).default([]),
  /** sha256 do corpo cru. */
  bodyHash: z.string().nullable(),
  dryRun: z.boolean().default(false),
  requestHeaders: z.record(z.string(), z.string()),
  requestBody: z.string(),
  bodyTruncated: z.boolean().default(false),
  responseBody: z.string(),
  durationMs: z.number().int().nonnegative(),
});
export type InboundEventLog = z.infer<typeof InboundEventLogSchema>;

// ---------------------------------------------------------------------------
// Portal — ações "inbound" da callable `devPortalWebhooks`
// ---------------------------------------------------------------------------

export const DevPortalInboundAction = {
  CONTEXT: 'inboundContext',
  CREATE: 'inboundCreate',
  UPDATE: 'inboundUpdate',
  SET_STATUS: 'inboundSetStatus',
  DELETE: 'inboundDelete',
  ROTATE_SECRET: 'inboundRotateSecret',
  LIST_EVENTS: 'inboundListEvents',
  GET_EVENT: 'inboundGetEvent',
  TEST: 'inboundTest',
  /** Todos os cenários numa chamada (botão "Run all"). */
  TEST_ALL: 'inboundTestAll',
} as const;
export type DevPortalInboundAction = (typeof DevPortalInboundAction)[keyof typeof DevPortalInboundAction];

/**
 * Só o campo `action` da entrada da callable — para despachar `inbound*` sem
 * cast de `request.data` (as duas famílias têm schemas próprios).
 */
export const DevPortalActionProbeSchema = z.looseObject({ action: z.string() });

/** A `action` é da família `inbound*`? Usado por: `devPortalWebhooks` para despachar. */
export function isDevPortalInboundAction(value: unknown): boolean {
  return typeof value === 'string' && (Object.values(DevPortalInboundAction) as string[]).includes(value);
}

const PortalKeyIdSchema = z.string().min(1).max(64);
const PortalEndpointIdSchema = z.string().min(1).max(64);
/** Mesmo formato da Idempotency-Key das ações da saída (`WebhookIdempotencyKeySchema`). */
const PortalIdempotencyKeySchema = z.string().min(8).max(128).regex(/^[A-Za-z0-9_-]+$/, 'invalid_idempotency_key');
const PortalEventsSchema = z
  .array(InboundWebhookEventTypeSchema)
  .min(1, 'events_required')
  .refine((arr) => new Set(arr).size === arr.length, 'events_duplicated');

export const DevPortalInboundInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal(DevPortalInboundAction.CONTEXT) }),
  z.object({
    action: z.literal(DevPortalInboundAction.CREATE),
    keyId: PortalKeyIdSchema,
    description: z.string().trim().max(200).default(''),
    events: PortalEventsSchema,
    idempotencyKey: PortalIdempotencyKeySchema,
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.UPDATE),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    description: z.string().trim().max(200).optional(),
    events: PortalEventsSchema.optional(),
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.SET_STATUS),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    status: InboundEndpointStatusSchema,
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.DELETE),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.ROTATE_SECRET),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    idempotencyKey: PortalIdempotencyKeySchema,
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.LIST_EVENTS),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    pageToken: z.string().max(200).optional(),
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.GET_EVENT),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    eventId: z.string().min(1).max(200),
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.TEST),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
    scenarioId: z.string().min(1).max(80),
    /** Corpo editado na tela (cenário personalizado). Assinado como o parceiro assinaria. */
    bodyOverride: z.string().max(INBOUND_MAX_BODY_BYTES * 2).optional(),
  }),
  z.object({
    action: z.literal(DevPortalInboundAction.TEST_ALL),
    keyId: PortalKeyIdSchema,
    endpointId: PortalEndpointIdSchema,
  }),
]);
export type DevPortalInboundInput = z.input<typeof DevPortalInboundInputSchema>;
export type DevPortalInboundParsedInput = z.infer<typeof DevPortalInboundInputSchema>;

export type PortalInboundEndpoint = Omit<InboundEndpoint, 'createdByEmail'> & {
  /** URL completa que o parceiro chama. */
  url: string;
};

export type DevPortalInboundContextResult = {
  endpoints: PortalInboundEndpoint[];
  maxEndpointsPerKey: number;
};
export type DevPortalInboundSecretResult = { endpoint: PortalInboundEndpoint; secret: string };
export type DevPortalInboundEndpointResult = { endpoint: PortalInboundEndpoint };
export type DevPortalInboundEventsResult = {
  events: (Omit<InboundEventLog, 'requestHeaders' | 'requestBody' | 'responseBody'> & { id: string })[];
  nextPageToken: string | null;
};
export type DevPortalInboundEventDetailResult = { event: InboundEventLog & { id: string } };

/** Resultado de um disparo da bancada — tudo que o parceiro precisa ver. */
export type InboundTestRun = {
  scenarioId: string;
  request: { method: string; url: string; headers: Record<string, string>; body: string };
  response: { status: number; headers: Record<string, string>; body: string };
  trace: InboundTraceStep[];
  /** Check que decidiu. */
  checkId: string;
  /** O cenário deu o resultado anunciado? (`false` em cenário personalizado que divergiu.) */
  matchedExpectation: boolean;
  durationMs: number;
  /** Mesmo cenário, da máquina do parceiro, com dry-run (segredo como placeholder). */
  curl: string;
};

export type DevPortalInboundTestAllResult = { runs: InboundTestRun[] };
